import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  apiLoginUser,
  createUserActivityAPI,
  getUserActivityAPI,
  editUserAPI,
  getLoggedInUserAPI,
  deleteUserActivityAPI,
  createUserAPI,
} from "./userAPI";
import { clearAssistants } from "../assistant/assistantSlice";

const initialState = {
  user: null,
  activity: [],
  users: [],
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async ({ email, password }, thunkAPI) => {
    try {
      const response = await apiLoginUser({ email, password });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Login failed",
      );
    }
  },
);

export const getLoggedInUser = createAsyncThunk(
  "user/getLoggedInUser",
  async (uuid, thunkAPI) => {
    try {
      const res = await getLoggedInUserAPI(uuid);
      const user = res.data?.user;

      if (!user) {
        return thunkAPI.rejectWithValue("User not found");
      }

      return user;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message,
      );
    }
  },
);

export const createUserActivity = createAsyncThunk(
  "user/createActivity",
  async (payload, thunkAPI) => {
    try {
      const res = await createUserActivityAPI(payload);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getUserActivity = createAsyncThunk(
  "user/getActivity",
  async (_, thunkAPI) => {
    try {
      const res = await getUserActivityAPI();
      return res.data.activities;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editUser = createAsyncThunk(
  "user/editUser",
  async ({ uuid, data }, thunkAPI) => {
    try {
      const res = await editUserAPI({ uuid, data });
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteUserActivity = createAsyncThunk(
  "user/deleteUserActivity",
  async ({ assistantId, userId }, thunkAPI) => {
    try {
      const res = await deleteUserActivityAPI({ assistantId, userId });
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const createUser = createAsyncThunk(
  "user/createUser",
  async ({ data }, thunkAPI) => {
    try {
      const res = await createUserAPI({ data });
      return res.user;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to create setting",
      );
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.activity = [];
      clearAssistants();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          ...action.payload,
          org: action.payload.organizationUuid,
        };
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getLoggedInUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getLoggedInUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createUserActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserActivity.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createUserActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getUserActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.activity = action.payload;
      })
      .addCase(getUserActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }) 

      .addCase(editUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUser = action.payload.user ?? action.payload;
        state.user = {
          ...state.user,
          ...updatedUser,
        };
      })
      .addCase(editUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteUserActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserActivity.fulfilled, (state) => {
        state.loading = false;
        state.activity = [];
      })
      .addCase(deleteUserActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUser, clearUser } = userSlice.actions;

export default userSlice.reducer;
